import {register} from '@shopify/theme-sections';

const sectionType = 'hero-banner';
const registeredSections = window.Shopify?.theme?.sections?.registered || {};
const isUnregistered = typeof registeredSections[sectionType] === 'undefined';

/**
 * Handles the carousel functionality for a single hero banner section
 * @param {HTMLElement} container - The hero banner container element
 */
function initCarousel(container) {
  const carousel = container.querySelector('.hero-banner__carousel');
  const slides = container.querySelectorAll('.hero-banner__carousel-slide');
  const leftButton = container.querySelector('.btn__left');
  const rightButton = container.querySelector('.btn__right');
  const totalSlides = slides.length;
  let currentSlide = 0;
  let autoplay;

  /**
   * Generates a random rotation value between -3 and 3 degrees
   * @returns {number} Random rotation value
   */
  function getRandomRotation() {
    return Math.floor(Math.random() * 6) - 3;
  }

  /**
   * Applies random rotation and position to all slides
   */
  function applyRandomRotationAndPosition() {
    slides.forEach((slide) => {
      const rotation = getRandomRotation();
      slide.style.transform = `rotate(${rotation}deg) translateX(0px) translateY(0px)`;
    });
  }

  /**
   * Navigates to a specific slide
   * @param {number} slideIndex - Index of the slide to navigate to
   */
  function goToSlide(slideIndex) {
    let targetSlide = slideIndex;
    if (targetSlide < 0) targetSlide = totalSlides - 1;
    if (targetSlide >= totalSlides) targetSlide = 0;

    carousel.style.transform = `translateX(-${targetSlide * 100}%)`;
    applyRandomRotationAndPosition();

    slides.forEach((slide, index) => {
      const imageWrapper = slide.querySelector('.component-image__wrapper');
      if (imageWrapper) {
        imageWrapper.style.opacity = index === targetSlide ? 1 : 0;
      }
      slide.classList.toggle('active', index === targetSlide);
    });

    currentSlide = targetSlide;

    // Reset autoplay interval when slide changes
    stopAutoplay();
    startAutoplay();
  }

  /**
   * Navigates to the next slide
   */
  function goToNextSlide() {
    goToSlide(currentSlide + 1);
  }

  /**
   * Navigates to the previous slide
   */
  function goToPreviousSlide() {
    goToSlide(currentSlide - 1);
  }

  /**
   * Starts the autoplay functionality
   */
  function startAutoplay() {
    autoplay = setInterval(goToNextSlide, 3000);
  }

  /**
   * Stops the autoplay functionality
   */
  function stopAutoplay() {
    clearInterval(autoplay);
  }

  // Add event listeners
  if (leftButton) leftButton.addEventListener('click', goToPreviousSlide);
  if (rightButton) rightButton.addEventListener('click', goToNextSlide);
  if (carousel) {
    carousel.addEventListener('mouseenter', stopAutoplay);
    carousel.addEventListener('mouseleave', startAutoplay);
  }

  // Initialize carousel
  if (slides.length > 0) {
    applyRandomRotationAndPosition();
    // Set first slide as active
    slides[0].classList.add('active');
    startAutoplay();
  }
}

export default isUnregistered &&
  register(sectionType, {
    onLoad() {
      const selectors = {
        parallaxImage: '[data-parallax-image]',
      };

      this.parallaxImage = this.container.querySelector(selectors.parallaxImage);
      this.sectionPosition = this.container.dataset.sectionPosition || 'top';

      // Initialize carousel for this section
      initCarousel(this.container);

      if (this.parallaxImage) {
        window.addEventListener('scroll', this.throttle(this.parallax.bind(this), 14));
      }
    },

    // Add throttle to increase performance
    throttle(fn, wait) {
      let time = Date.now();
      return function () {
        if (!(time + wait - Date.now() < 0)) return;
        fn();
        time = Date.now();
      };
    },

    parallax() {
      let scrolled = 0;
      let coords = 0;

      if (this.sectionPosition === 'top') {
        scrolled = window.scrollY;
        coords = `${scrolled * 0.5}px`;
      }

      if (this.sectionPosition === 'bottom' && window.innerHeight - this.container.getBoundingClientRect().top > 0) {
        scrolled = window.innerHeight - this.container.getBoundingClientRect().top;
        coords = `${scrolled * 0.2 - 60}px`;
      }

      if (scrolled === 0 && coords === 0) return;

      this.parallaxImage.style.transform = `translateY(${coords})`;
    },
  });
